import { useState, useEffect, useCallback } from "react";
import {
  RefreshCw,
  Link2,
  Link2Off,
  Star,
  ShieldAlert,
  CheckCircle2,
  XCircle,
  HelpCircle,
  Loader2,
  Megaphone,
  BarChart3,
  Search,
  UserPlus,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { API_URL } from "../../utils/api";

const BASE = `${API_URL}/api/admin/google-reporting`;

const SOURCE_META = {
  ads: { label: "Google Ads", icon: Megaphone },
  ga4: { label: "Google Analytics (GA4)", icon: BarChart3 },
  gsc: { label: "Search Console", icon: Search },
};

// ── Punycode-avkodning (RFC 3492) — endast för läsbar visning ─────────────
function decodePunycode(input) {
  const base = 36, tMin = 1, tMax = 26, skew = 38, damp = 700;
  const initialBias = 72, initialN = 128;
  const baseMinusTMin = base - tMin;

  function adapt(delta, numPoints, firstTime) {
    delta = firstTime ? Math.floor(delta / damp) : delta >> 1;
    delta += Math.floor(delta / numPoints);
    let k = 0;
    for (; delta > ((baseMinusTMin * tMax) >> 1); k += base) {
      delta = Math.floor(delta / baseMinusTMin);
    }
    return Math.floor(k + ((baseMinusTMin + 1) * delta) / (delta + skew));
  }
  function decodeDigit(cp) {
    if (cp - 48 < 10) return cp - 22;
    if (cp - 65 < 26) return cp - 65;
    if (cp - 97 < 26) return cp - 97;
    return base;
  }

  const output = [];
  let i = 0, n = initialN, bias = initialBias;
  const delim = input.lastIndexOf("-");
  const basic = delim < 0 ? 0 : delim;
  for (let j = 0; j < basic; j++) output.push(input.charCodeAt(j));
  let index = basic > 0 ? basic + 1 : 0;

  while (index < input.length) {
    const oldi = i;
    let w = 1;
    for (let k = base; ; k += base) {
      if (index >= input.length) return input; // ogiltig — returnera rå
      const digit = decodeDigit(input.charCodeAt(index++));
      i += digit * w;
      const t = k <= bias ? tMin : k >= bias + tMax ? tMax : k - bias;
      if (digit < t) break;
      w *= base - t;
    }
    const out = output.length + 1;
    bias = adapt(i - oldi, out, oldi === 0);
    n += Math.floor(i / out);
    i %= out;
    output.splice(i++, 0, n);
  }
  try {
    return String.fromCodePoint(...output);
  } catch {
    return input;
  }
}

function decodeHost(host) {
  return host
    .split(".")
    .map((l) => (l.toLowerCase().startsWith("xn--") ? decodePunycode(l.slice(4)) : l))
    .join(".");
}

// Läsbar domän för GSC (behåll exakt resource_id för backend-anrop)
function humanizeGsc(siteUrl) {
  if (!siteUrl) return siteUrl;
  try {
    if (siteUrl.startsWith("sc-domain:")) {
      return decodeHost(siteUrl.slice("sc-domain:".length));
    }
    if (siteUrl.startsWith("http")) {
      const u = new URL(siteUrl);
      return siteUrl.replace(u.hostname, decodeHost(u.hostname));
    }
    return decodeHost(siteUrl);
  } catch {
    return siteUrl;
  }
}

function connKey(source, resourceId, subresourceId) {
  return `${source}|${resourceId}|${subresourceId || ""}`;
}

const STATUS_BADGE = {
  connected: { cls: "bg-green-100 text-green-800", icon: CheckCircle2, label: "Verifierad" },
  error: { cls: "bg-red-100 text-red-800", icon: XCircle, label: "Fel" },
  unverified: { cls: "bg-gray-100 text-gray-600", icon: HelpCircle, label: "Ej verifierad" },
};

export default function GoogleConnections() {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [clients, setClients] = useState([]);
  const [discovery, setDiscovery] = useState({ ads: [], ga4: [], gsc: [], errors: {} });
  const [connByKey, setConnByKey] = useState({});
  const [selectedClient, setSelectedClient] = useState({}); // itemKey -> client_key
  const [busy, setBusy] = useState({}); // rowId -> bool
  const [metaForm, setMetaForm] = useState({ clientKey: "", account: "", primary: true });
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClientForm, setNewClientForm] = useState({ name: "", clientKey: "" });

  const authFetch = useCallback(
    (path, opts = {}) =>
      fetch(`${BASE}${path}`, {
        ...opts,
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
          ...(opts.headers || {}),
        },
      }),
    [getAuthHeaders]
  );

  const loadAll = useCallback(
    async (isSync = false) => {
      isSync ? setSyncing(true) : setLoading(true);
      try {
        const [clientsRes, discRes] = await Promise.all([
          authFetch("/clients"),
          authFetch("/google/discovery"),
        ]);
        if (!clientsRes.ok || !discRes.ok) {
          throw new Error("Kunde inte hämta data");
        }
        const clientsData = await clientsRes.json();
        const discData = await discRes.json();
        setClients(clientsData);
        setDiscovery(discData);

        // Hämta ALLA connections globalt → uppslag per resurs (visar även
        // orphans vars client saknas, så redan kopplade resurser inte felaktigt
        // visas som "Koppla" och kan hanteras/kopplas bort).
        const connRes = await authFetch("/connections");
        const allConns = connRes.ok ? await connRes.json() : [];
        const map = {};
        allConns.forEach((conn) => {
          map[connKey(conn.source, conn.resource_id, conn.subresource_id)] = conn;
        });
        setConnByKey(map);
      } catch (e) {
        console.error(e);
        toast.error("Kunde inte ladda Google-kopplingar");
      } finally {
        isSync ? setSyncing(false) : setLoading(false);
      }
    },
    [authFetch]
  );

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const setRowBusy = (id, v) => setBusy((prev) => ({ ...prev, [id]: v }));

  const linkResource = async (item) => {
    const key = connKey(item.source, item.resource_id, item.subresource_id);
    const clientKey = selectedClient[key];
    if (!clientKey) {
      toast.error("Välj en kund först");
      return;
    }
    setRowBusy(key, true);
    try {
      const res = await authFetch(
        `/clients/${encodeURIComponent(clientKey)}/connections`,
        {
          method: "POST",
          body: JSON.stringify({
            source: item.source,
            resource_id: item.resource_id,
            subresource_id: item.subresource_id || null,
            display_name: item.display_name || item.resource_id,
            metadata: {},
          }),
        }
      );
      if (res.ok) {
        toast.success("Resurs kopplad");
        await loadAll(true);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || err.error || "Kunde inte koppla resursen");
      }
    } catch (e) {
      toast.error("Kunde inte koppla resursen");
    } finally {
      setRowBusy(key, false);
    }
  };

  const updateConnection = async (conn, payload, successMsg) => {
    setRowBusy(conn.connection_id, true);
    try {
      const res = await authFetch(`/connections/${conn.connection_id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success(successMsg);
        await loadAll(true);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Åtgärden misslyckades");
      }
    } catch (e) {
      toast.error("Åtgärden misslyckades");
    } finally {
      setRowBusy(conn.connection_id, false);
    }
  };

  const disableConnection = (conn) => {
    if (!window.confirm(`Koppla bort "${conn.display_name}"? Kopplingen inaktiveras (raderas inte).`)) {
      return;
    }
    updateConnection(conn, { enabled: false }, "Koppling inaktiverad");
  };

  const setPrimary = (conn) => updateConnection(conn, { primary: true }, "Primär koppling uppdaterad");

  const createClient = async () => {
    const name = (newClientForm.name || "").trim();
    const clientKey = (newClientForm.clientKey || "").trim().toLowerCase();
    if (!name || !clientKey) {
      toast.error("Ange både namn och client_key");
      return;
    }
    setRowBusy("new-client", true);
    try {
      // Endast client_key + name — Google Ads/GA4/GSC lämnas tomma/inaktiva.
      const res = await authFetch("/clients", {
        method: "POST",
        body: JSON.stringify({ client_key: clientKey, name }),
      });
      if (res.ok) {
        toast.success(`Kund "${name}" skapad`);
        setShowNewClient(false);
        setNewClientForm({ name: "", clientKey: "" });
        await loadAll(true);
      } else if (res.status === 409) {
        toast.error(`client_key "${clientKey}" finns redan — välj ett annat`);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || err.error || "Kunde inte skapa kunden");
      }
    } catch (e) {
      toast.error("Kunde inte skapa kunden");
    } finally {
      setRowBusy("new-client", false);
    }
  };

  const addMetaConnection = async () => {
    const clientKey = (metaForm.clientKey || "").trim();
    let account = (metaForm.account || "").trim();
    if (!clientKey || !account) {
      toast.error("Ange både kund och Meta-annonskonto");
      return;
    }
    if (!account.startsWith("act_")) account = `act_${account.replace(/^act_?/, "")}`;
    setRowBusy("meta-add", true);
    try {
      const res = await authFetch(
        `/clients/${encodeURIComponent(clientKey)}/connections?create_client=true`,
        {
          method: "POST",
          body: JSON.stringify({
            source: "meta",
            resource_id: account,
            subresource_id: null,
            display_name: `${clientKey} (Meta Ads)`,
            metadata: {},
            primary: metaForm.primary,
          }),
        }
      );
      if (res.ok) {
        toast.success("Meta-konto kopplat");
        setMetaForm({ clientKey: "", account: "", primary: true });
        await loadAll(true);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || err.error || "Kunde inte koppla Meta-kontot");
      }
    } catch (e) {
      toast.error("Kunde inte koppla Meta-kontot");
    } finally {
      setRowBusy("meta-add", false);
    }
  };

  const verifyConnection = async (conn) => {
    setRowBusy(conn.connection_id, true);
    try {
      const res = await authFetch(`/connections/${conn.connection_id}/verify`, { method: "POST" });
      if (res.ok) {
        const updated = await res.json();
        if (updated.status === "connected") toast.success(conn.source === "meta" ? "Meta-verifiering godkänd" : "Verifierad mot Google");
        else toast.error("Verifiering misslyckades (behörighet/API)");
        await loadAll(true);
      } else {
        toast.error("Verifiering misslyckades");
      }
    } catch (e) {
      toast.error("Verifiering misslyckades");
    } finally {
      setRowBusy(conn.connection_id, false);
    }
  };

  const clientName = (key) => clients.find((c) => c.client_key === key)?.name || key;

  const isLinkable = (item) => {
    if (item.source === "ads") return item.reportable === true;
    return true;
  };

  const unlinkableReason = (item) => {
    if (item.source === "ads") {
      if (item.manager) return "Manager-konto (MCC) — kan inte kopplas som rapportkälla";
      if (item.test_account) return "Testkonto — kan inte kopplas";
      if (item.status !== "ENABLED") return `Status: ${item.status} — kan inte kopplas`;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="google-connections-loading">
        <Loader2 className="w-6 h-6 text-[#255E69] animate-spin" />
        <span className="ml-3 text-gray-500">Laddar Google-kopplingar…</span>
      </div>
    );
  }

  return (
    <div data-testid="google-connections">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Google-kopplingar</h1>
          <p className="text-sm text-gray-500 mt-1">
            Koppla Googles resurser (Ads, GA4, Search Console) till Elidos kunder. Läs-endast — inga
            ändringar görs hos Google.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowNewClient(true)}
            data-testid="new-client-button"
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#255E69] text-[#255E69] rounded-lg text-sm font-medium hover:bg-[#255E69]/5 transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            Ny kund
          </button>
          <button
            onClick={() => loadAll(true)}
            disabled={syncing}
            data-testid="sync-google-button"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#255E69] text-white rounded-lg text-sm font-medium hover:bg-[#1e4d56] transition-colors disabled:opacity-60"
          >
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {syncing ? "Synkar…" : "Synka Google"}
          </button>
        </div>
      </div>

      {/* Ny Google Reporting-kund — enkel modal */}
      {showNewClient && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          data-testid="new-client-modal"
          onClick={() => !busy["new-client"] && setShowNewClient(false)}
        >
          <div
            className="w-full max-w-md rounded-xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
              <div className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-[#255E69]" />
                <h2 className="text-base font-semibold text-gray-900">Ny kund</h2>
              </div>
              <button
                onClick={() => setShowNewClient(false)}
                disabled={busy["new-client"]}
                data-testid="new-client-close"
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-5 space-y-4">
              <p className="text-sm text-gray-500">
                Skapa en Google Reporting-kund. Google Ads/GA4/Search Console kopplas separat efteråt.
              </p>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Namn</label>
                <input
                  type="text"
                  value={newClientForm.name}
                  onChange={(e) => setNewClientForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="t.ex. ALJO Bygg"
                  data-testid="new-client-name-input"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#255E69]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">client_key</label>
                <input
                  type="text"
                  value={newClientForm.clientKey}
                  onChange={(e) => setNewClientForm((f) => ({ ...f, clientKey: e.target.value }))}
                  placeholder="t.ex. aljo"
                  data-testid="new-client-key-input"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono lowercase focus:outline-none focus:ring-2 focus:ring-[#255E69]"
                />
                <p className="mt-1 text-xs text-gray-400">Endast gemener, siffror och bindestreck (minst 2 tecken).</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-5 py-4">
              <button
                onClick={() => setShowNewClient(false)}
                disabled={busy["new-client"]}
                data-testid="new-client-cancel"
                className="px-4 py-2 text-sm text-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Avbryt
              </button>
              <button
                onClick={createClient}
                disabled={busy["new-client"]}
                data-testid="new-client-save"
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#255E69] text-white rounded-lg text-sm font-medium hover:bg-[#1e4d56] transition-colors disabled:opacity-60"
              >
                {busy["new-client"] ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                Skapa kund
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Discovery errors */}
      {discovery.errors && Object.keys(discovery.errors).length > 0 && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="discovery-errors">
          <div className="flex items-start gap-2">
            <ShieldAlert className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Vissa källor kunde inte hämtas från Google:</p>
              <ul className="mt-1 list-disc list-inside">
                {Object.entries(discovery.errors).map(([src, err]) => (
                  <li key={src}>
                    {SOURCE_META[src]?.label || src}: {err}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Meta (Facebook) Ads — manuell koppling per kund */}
      <div className="mb-8 rounded-xl border border-gray-200 bg-white p-5" data-testid="meta-connections-card">
        <div className="flex items-center gap-2 mb-1">
          <Megaphone className="w-5 h-5 text-[#1877F2]" />
          <h2 className="text-base font-semibold text-gray-900">Meta (Facebook) Ads</h2>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Koppla en kunds Meta-annonskonto (act_…) till en client_key. Använder Elidos gemensamma
          Meta System User-token (server-secret) — ingen token per kund. Läs-endast.
        </p>

        {/* Befintliga Meta-kopplingar */}
        <div className="space-y-2 mb-5" data-testid="meta-connection-list">
          {Object.values(connByKey).filter((c) => c.source === "meta" && c.enabled).length === 0 ? (
            <p className="text-sm text-gray-400">Inga Meta-konton kopplade ännu.</p>
          ) : (
            Object.values(connByKey)
              .filter((c) => c.source === "meta" && c.enabled)
              .map((conn) => (
                <div
                  key={conn.connection_id}
                  data-testid={`meta-connection-${conn.client_key}`}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2"
                >
                  <div className="min-w-0">
                    <span className="font-medium text-gray-900">{conn.client_key}</span>
                    <span className="mx-2 text-gray-300">→</span>
                    <span className="font-mono text-sm text-gray-700">{conn.resource_id}</span>
                    {conn.primary && (
                      <span className="ml-2 inline-flex items-center gap-1 text-xs text-amber-700">
                        <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> primär
                      </span>
                    )}
                    <span className={conn.status === "connected" ? "ml-2 text-xs text-green-700" : "ml-2 text-xs text-gray-500"}>
                      {conn.status === "connected" ? "Verifierad" : conn.status === "error" ? "Verifiering misslyckades" : "Ej verifierad"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => verifyConnection(conn)}
                      disabled={Boolean(busy[conn.connection_id])}
                      className="rounded border border-gray-200 bg-white px-2 py-1 text-xs hover:bg-gray-50"
                      data-testid={"verify-meta-" + conn.client_key}
                    >
                      Verifiera Meta
                    </button>
                    {!conn.primary && (
                      <button
                        onClick={() => setPrimary(conn)}
                        disabled={busy[conn.connection_id]}
                        data-testid={`meta-set-primary-${conn.client_key}`}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-gray-200 hover:bg-white"
                      >
                        <Star className="w-3 h-3" /> Sätt primär
                      </button>
                    )}
                    <button
                      onClick={() => disableConnection(conn)}
                      disabled={busy[conn.connection_id]}
                      data-testid={`meta-unlink-${conn.client_key}`}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50"
                    >
                      <Link2Off className="w-3 h-3" /> Koppla bort
                    </button>
                  </div>
                </div>
              ))
          )}
        </div>

        {/* Lägg till Meta-konto */}
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end border-t border-gray-100 pt-4">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">Kund (client_key)</label>
            <input
              type="text"
              list="meta-client-options"
              value={metaForm.clientKey}
              onChange={(e) => setMetaForm((f) => ({ ...f, clientKey: e.target.value }))}
              placeholder="t.ex. lauremonte"
              data-testid="meta-client-input"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#255E69]"
            />
            <datalist id="meta-client-options">
              {clients.map((c) => (
                <option key={c.client_key} value={c.client_key}>{c.name || c.client_key}</option>
              ))}
            </datalist>
          </div>
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">Meta-annonskonto (act_…)</label>
            <input
              type="text"
              value={metaForm.account}
              onChange={(e) => setMetaForm((f) => ({ ...f, account: e.target.value }))}
              placeholder="act_1092124136503063"
              data-testid="meta-account-input"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#255E69]"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
            <input
              type="checkbox"
              checked={metaForm.primary}
              onChange={(e) => setMetaForm((f) => ({ ...f, primary: e.target.checked }))}
              data-testid="meta-primary-checkbox"
            />
            Primär
          </label>
          <button
            onClick={addMetaConnection}
            disabled={busy["meta-add"]}
            data-testid="meta-add-button"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#255E69] text-white rounded-lg text-sm font-medium hover:bg-[#1e4d56] transition-colors disabled:opacity-60"
          >
            {busy["meta-add"] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
            Lägg till Meta-konto
          </button>
        </div>
      </div>

      {/* Källor */}
      <div className="space-y-8">
        {["ads", "ga4", "gsc"].map((source) => {
          const meta = SOURCE_META[source];
          const SourceIcon = meta.icon;
          const items = discovery[source] || [];
          return (
            <div
              key={source}
              className="bg-white rounded-lg shadow-sm border border-gray-100"
              data-testid={`source-section-${source}`}
            >
              <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
                <div className="w-9 h-9 rounded-lg bg-[#255E69]/10 flex items-center justify-center">
                  <SourceIcon className="w-5 h-5 text-[#255E69]" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">{meta.label}</h2>
                  <p className="text-xs text-gray-400">{items.length} resurser hittade</p>
                </div>
              </div>

              {items.length === 0 ? (
                <div className="px-6 py-8 text-center text-sm text-gray-400">
                  Inga resurser hittades för denna källa.
                </div>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {items.map((item) => {
                    const key = connKey(item.source, item.resource_id, item.subresource_id);
                    const conn = connByKey[key];
                    const linked = !!conn && conn.enabled;
                    const displayName =
                      source === "gsc" ? humanizeGsc(item.resource_id) : item.display_name;
                    const linkable = isLinkable(item);
                    const reason = unlinkableReason(item);
                    const rowBusy = busy[key] || (conn && busy[conn.connection_id]);
                    const statusInfo =
                      conn && STATUS_BADGE[conn.status] ? STATUS_BADGE[conn.status] : STATUS_BADGE.unverified;
                    const StatusIcon = statusInfo.icon;

                    return (
                      <li
                        key={key}
                        className="px-6 py-4 flex flex-col lg:flex-row lg:items-center gap-4"
                        data-testid={`resource-row-${item.resource_id}`}
                      >
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-gray-900 truncate">{displayName}</span>
                            {source === "ads" && item.manager && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-700">
                                <ShieldAlert className="w-3 h-3" /> Manager (MCC)
                              </span>
                            )}
                            {source === "ads" && item.test_account && (
                              <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                                Testkonto
                              </span>
                            )}
                            {linked && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-[#255E69]/10 text-[#255E69]">
                                <Link2 className="w-3 h-3" /> {clientName(conn.client_key)}
                              </span>
                            )}
                            {linked && conn.primary && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">
                                <Star className="w-3 h-3 fill-amber-500 text-amber-500" /> Primär
                              </span>
                            )}
                            {linked && (
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${statusInfo.cls}`}>
                                <StatusIcon className="w-3 h-3" /> {statusInfo.label}
                              </span>
                            )}
                          </div>
                          <div className="mt-1 flex items-center gap-3 text-xs text-gray-400 font-mono">
                            <span className="truncate">{item.resource_id}</span>
                            {item.currency && <span>· {item.currency}</span>}
                            {item.permission_level && <span>· {item.permission_level}</span>}
                            {item.property_type && <span>· {item.property_type}</span>}
                          </div>
                          {conn && conn.status === "error" && conn.last_error && (
                            <p className="mt-1 text-xs text-red-500">Senaste fel: {conn.last_error}</p>
                          )}
                        </div>

                        {/* Åtgärder */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {linked ? (
                            <>
                              <button
                                onClick={() => verifyConnection(conn)}
                                disabled={rowBusy}
                                data-testid={`verify-connection-${item.resource_id}`}
                                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-60"
                              >
                                {rowBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                                Verifiera
                              </button>
                              {!conn.primary && (
                                <button
                                  onClick={() => setPrimary(conn)}
                                  disabled={rowBusy}
                                  data-testid={`set-primary-${item.resource_id}`}
                                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-60"
                                >
                                  <Star className="w-4 h-4" />
                                  Gör primär
                                </button>
                              )}
                              <button
                                onClick={() => disableConnection(conn)}
                                disabled={rowBusy}
                                data-testid={`unlink-connection-${item.resource_id}`}
                                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-red-200 rounded-lg text-red-600 hover:bg-red-50 transition-colors disabled:opacity-60"
                              >
                                <Link2Off className="w-4 h-4" />
                                Koppla bort
                              </button>
                            </>
                          ) : !linkable ? (
                            <span
                              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs text-gray-400"
                              data-testid={`not-linkable-${item.resource_id}`}
                              title={reason || ""}
                            >
                              <ShieldAlert className="w-4 h-4" />
                              {reason || "Kan inte kopplas"}
                            </span>
                          ) : (
                            <>
                              <select
                                value={selectedClient[key] || item.suggested_client_key || ""}
                                onChange={(e) =>
                                  setSelectedClient((prev) => ({ ...prev, [key]: e.target.value }))
                                }
                                data-testid={`client-select-${item.resource_id}`}
                                className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#255E69]/30 bg-white"
                              >
                                <option value="">Välj kund…</option>
                                {clients.map((c) => (
                                  <option key={c.client_key} value={c.client_key}>
                                    {c.name} {item.suggested_client_key === c.client_key ? "(föreslagen)" : ""}
                                  </option>
                                ))}
                              </select>
                              <button
                                onClick={() => linkResource(item)}
                                disabled={rowBusy}
                                data-testid={`link-connection-${item.resource_id}`}
                                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-[#255E69] text-white rounded-lg hover:bg-[#1e4d56] transition-colors disabled:opacity-60"
                              >
                                {rowBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                                Koppla
                              </button>
                            </>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
